import React, { FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Loader from '@justgiving/loading/es/Loading';
import { useTracking } from '@justgiving/react-tracking';
import classNames from 'classnames';

import { PageStatus } from '../../../types/__generated__';
import { Experiments } from '../../constants/experiments';
import { useApp, useOptimizely, usePageMeta } from '../../hooks';
import useBasePageQuery from '../../hooks/useBasePageQuery';
import { useIsMounted } from '../../hooks/useIsMounted';
import isLightColor from '../../utils/colors/isLightColor';

import styles from './DonateButton.scss';
import { getIsMatchedGiving } from '../../utils/matchedGiving';

type DonateButtonProps = {
  className?: string;
  inactivePageBehaviour?: 'disable' | 'hide';
  onClick?: () => void;
  trackingDataPageSection?: string;
  experiments?: string[];
  successType?: string;
  label?: string;
  id?: string;
};

export const DonateButton: FunctionComponent<DonateButtonProps> = ({
  className,
  inactivePageBehaviour = 'disable',
  onClick = () => {},
  trackingDataPageSection,
  experiments,
  successType,
  label,
  id,
}) => {
  const basePageData = useBasePageQuery();
  const { currentUrl, pageType } = usePageMeta();
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation('common', { useSuspense: false });
  const track = useTracking();
  const optimizely = useOptimizely();
  const isMounted = useIsMounted();
  const { showCheckoutModal, showCheckoutPopup } = useApp();

  const urlParams = new URLSearchParams(
    isMounted ? window.location.search : '',
  );

  const handleClick = (event: any) => {
    // Preventing default in all cases, and manually redirecting to checkout, to see if this affects Optimizely bucketing
    event.preventDefault();
    let shouldRedirect = true;

    setLoading(true);

    try {
      const showEmbeddedCheckout = urlParams.get('embedCheckout') === 'true';

      const popupSideBySideWidthInPixels = 938;
      const popupSideBySideHeightInPixels = 640;
      const decision =
        showEmbeddedCheckout ||
        window?.screen.width < popupSideBySideWidthInPixels
          ? null
          : optimizely?.decide(
              Experiments.ConsumerFintech.CheckoutConversion.PopupSideBySide
                .flagKey,
            );
      const popupWidth = decision?.enabled ? popupSideBySideWidthInPixels : 600;
      const popupHeight =
        decision?.variationKey === 'c' ? popupSideBySideHeightInPixels : 850;

      const donateUrl = `${currentUrl}/donate${buildQueryString(true)}`;

      if (showEmbeddedCheckout) {
        setLoading(false);
        shouldRedirect = false;
        showCheckoutModal(donateUrl);
      }

      if (!showEmbeddedCheckout) {
        setLoading(false);
        shouldRedirect = false;
        showCheckoutPopup(donateUrl, popupWidth, popupHeight);
      }
    } catch (error) {
      // Do nothing
    }

    track({
      page_section: `${pageType} page > ${trackingDataPageSection ||
        'unknown'}`.toLowerCase(),
      event: 'link click',
      action_name: 'link click',
      event_value: 'donate',
      subtype: 'donate',
    });

    if (optimizely) {
      optimizely.track(Experiments.TrackingEvents.donateButtonClick);
    }

    onClick();

    // Preventing default in all cases, and manually redirecting to checkout, to see if this affects Optimizely bucketing
    if (shouldRedirect)
      window.location.assign(`${currentUrl}/donate${buildQueryString()}`);
  };

  type ActiveDonateButtonProps = {
    className?: string;
    href: string;
    id?: string;
  };

  const ActiveDonateButton = ({
    className,
    href,
    id,
  }: ActiveDonateButtonProps) => {
    return (
      <a
        className={className}
        onClick={handleClick}
        href={href}
        data-qa="donateButton"
        id={id}
      >
        {loading ? (
          <Loader size="small" colour="white" />
        ) : (
          <>
            {label ?? t('common.donateButton')}
            {isMatched && <span className={styles.doubleMatchIcon} />}
          </>
        )}
      </a>
    );
  };

  if (!basePageData.data?.page) {
    return (
      <ActiveDonateButton
        href={`${currentUrl}/donate`}
        className={classNames(
          'jg-btn',
          'jg-btn--themed',
          styles.button,
          className,
        )}
        id={id}
      />
    );
  }

  const {
    status,
    relationships,
    branding,
    donationSummary,
  } = basePageData.data.page;

  const isDonateActive = status === PageStatus.Active;

  const isMultiBeneficiaryCampaign =
    relationships && relationships.beneficiaries.totalCount > 1;

  if (inactivePageBehaviour === 'hide' && !isDonateActive) {
    return null;
  }

  const buttonType =
    branding?.primaryColor && isLightColor(branding.primaryColor)
      ? 'jg-btn--ghost'
      : 'jg-btn--themed';

  const isMatched = getIsMatchedGiving(donationSummary);

  const buildQueryString = (showEmbeddedCheckout?: boolean) => {
    const donateUrlParams = new URLSearchParams();

    const trimmedSuccessType = successType?.trim();
    const trimmedExperiments = experiments
      ?.map(e => e.trim())
      .filter(e => e !== '');

    if (
      trimmedExperiments &&
      trimmedExperiments.length > 0 &&
      trimmedSuccessType
    ) {
      donateUrlParams.append('experiments', trimmedExperiments.join(','));
      donateUrlParams.append('successType', trimmedSuccessType);
    }

    if (showEmbeddedCheckout) {
      donateUrlParams.append('checkoutMode', 'Headless');
    }

    const utmSource = urlParams.get('utm_source');
    if (utmSource) {
      donateUrlParams.append('reference', utmSource);
    }

    return donateUrlParams.toString() ? `?${donateUrlParams.toString()}` : '';
  };

  const donateLink = isDonateActive ? (
    <ActiveDonateButton
      className={classNames('jg-btn', buttonType, styles.button, className)}
      href={`${currentUrl}/donate${buildQueryString()}`}
      id={id}
    />
  ) : (
    <>
      <div
        className={classNames(
          'jg-btn',
          'jg-text--link',
          buttonType,
          styles.button,
          className,
          styles.disabled,
        )}
        data-qa="donateButtonDisabled"
      >
        <span aria-hidden>{label ?? t('common.donateButton')}</span>
        <span className="jg-sr-only">{t('common.donateButtonInactive')}</span>
      </div>
    </>
  );

  return !isMultiBeneficiaryCampaign ? donateLink : null;
};

export default DonateButton;
